import { createClient, type PostgrestError } from '@supabase/supabase-js'
import type {
  CollectionSummary,
  NewWriting,
  Profile,
  PublishedPayload,
  PublishOptions,
  Thought,
  ThoughtQuery,
  Viewer,
  Writing,
  WritingPatch,
  WritingVersion,
} from '../types'
import { startOfDay, calendarDaysAgo } from '../text'
import { imageExtension, assertUploadableImage } from '../images'
import { assertUploadableAudio, audioExtension, baseAudioType } from '../audio'
import type { WritingsBackend } from './types'

const WRITING_COLUMNS =
  'id,owner_id,kind,title,subtitle,cover_url,cover_frame,blocks,show_times,show_sources,status,visibility,slug,published_at,collection_id,share_collection,reflection_date,word_count,narration,created_at,updated_at'
const VERSION_COLUMNS = 'id,writing_id,reason,title,subtitle,cover_url,cover_frame,blocks,narration,word_count,created_at'
const THOUGHT_COLUMNS = 'id,title,body,source_url,source_title,image_path,created_at'
const IMAGE_BUCKET = 'writing-images'
const AUDIO_BUCKET = 'writing-audio'
const THOUGHT_IMAGE_BUCKET = 'thought-images'

function fail(error: PostgrestError | Error | null, fallback: string): never {
  const e = error as PostgrestError | null
  if (e && 'code' in e && e.code === '23505') throw new Error('You already have a writing at that link. Pick another.')
  throw new Error(error?.message || fallback)
}

function randomName(): string {
  const c = globalThis.crypto
  return typeof c?.randomUUID === 'function' ? c.randomUUID() : Math.random().toString(36).slice(2)
}

/** PostgREST `or` filter values: quote them and drop characters that break the grammar. */
function ilikeTerm(search: string): string {
  return search.replace(/["\\%*,()]/g, ' ').trim()
}

export function createSupabaseBackend(url: string, anonKey: string): WritingsBackend {
  // Its own storage key, so a Writings session never collides with the main
  // app's on a shared origin.
  const sb = createClient(url, anonKey, {
    auth: { storageKey: 'burrow-writings-auth', persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
  })

  const uid = async (): Promise<string | null> => {
    const { data } = await sb.auth.getSession()
    return data.session?.user.id ?? null
  }
  const requireUid = async (): Promise<string> => {
    const id = await uid()
    if (!id) throw new Error('Sign in to continue.')
    return id
  }

  const backend: WritingsBackend = {
    mode: 'supabase',

    async getViewer(): Promise<Viewer | null> {
      const { data } = await sb.auth.getSession()
      const user = data.session?.user
      return user ? { id: user.id, email: user.email ?? null } : null
    },

    onViewerChange(cb) {
      const { data } = sb.auth.onAuthStateChange((_event, session) => {
        cb(session?.user ? { id: session.user.id, email: session.user.email ?? null } : null)
      })
      return () => data.subscription.unsubscribe()
    },

    async signInWithPassword(email, password) {
      const { error } = await sb.auth.signInWithPassword({ email, password })
      if (error) fail(error, 'Could not sign in.')
    },

    async sendEmailCode(email) {
      const { error } = await sb.auth.signInWithOtp({ email, options: { shouldCreateUser: false } })
      if (error) fail(error, 'Could not send a code.')
    },

    async verifyEmailCode(email, code) {
      const { error } = await sb.auth.verifyOtp({ email, token: code, type: 'email' })
      if (error) fail(error, 'That code did not work.')
    },

    async signOut() {
      await sb.auth.signOut()
    },

    async getMyProfile(): Promise<Profile | null> {
      const me = await uid()
      if (!me) return null
      const { data, error } = await sb.from('profiles').select('id,handle,display_name,avatar_url').eq('id', me).maybeSingle()
      if (error) fail(error, 'Could not load your profile.')
      return (data as Profile | null) ?? { id: me, handle: null, display_name: null, avatar_url: null }
    },

    async listMyWritings() {
      const me = await requireUid()
      const { data, error } = await sb
        .from('writings')
        .select(WRITING_COLUMNS)
        .eq('owner_id', me)
        .order('updated_at', { ascending: false })
      if (error) fail(error, 'Could not load your writings.')
      return (data ?? []) as Writing[]
    },

    async getWriting(id) {
      const { data, error } = await sb.from('writings').select(WRITING_COLUMNS).eq('id', id).maybeSingle()
      if (error) fail(error, 'Could not load this writing.')
      return (data as Writing | null) ?? null
    },

    async createWriting(init: NewWriting) {
      const me = await requireUid()
      const { data, error } = await sb.from('writings').insert({ ...init, owner_id: me }).select(WRITING_COLUMNS).single()
      if (error) fail(error, 'Could not start a writing.')
      return data as Writing
    },

    async updateWriting(id, patch: WritingPatch) {
      const { error } = await sb.from('writings').update(patch).eq('id', id)
      if (error) fail(error, 'Could not save.')
    },

    async deleteWriting(id) {
      const { error } = await sb.from('writings').delete().eq('id', id)
      if (error) fail(error, 'Could not delete this writing.')
    },

    async isSlugAvailable(slug, exceptId) {
      const me = await requireUid()
      const { data, error } = await sb
        .from('writings')
        .select('id')
        .eq('owner_id', me)
        .eq('slug', slug)
        .neq('id', exceptId)
        .limit(1)
      if (error) fail(error, 'Could not check that link.')
      return (data ?? []).length === 0
    },

    async publishWriting(id, opts: PublishOptions) {
      const { data, error } = await sb
        .from('writings')
        .update({ status: 'published', ...opts })
        .eq('id', id)
        .select(WRITING_COLUMNS)
        .single()
      if (error) fail(error, 'Could not publish.')
      // History keeps what went live; a failed snapshot mustn't undo a publish.
      await backend.snapshotWriting(id, 'publish').catch(() => {})
      return data as Writing
    },

    async unpublishWriting(id) {
      // The slug is kept, so republishing restores the same link.
      const { error } = await sb.from('writings').update({ status: 'draft' }).eq('id', id)
      if (error) fail(error, 'Could not unpublish.')
    },

    async listThoughts({ search, day, limit }: ThoughtQuery) {
      const me = await requireUid()
      let q = sb
        .from('thoughts')
        .select(THOUGHT_COLUMNS)
        .eq('owner_id', me)
        .order('created_at', { ascending: false })
        .limit(limit ?? 60)
      if (day !== undefined) {
        q = q.gte('created_at', startOfDay(day).toISOString()).lt('created_at', startOfDay(day - 1).toISOString())
      }
      const term = search ? ilikeTerm(search) : ''
      if (term) q = q.or(`body.ilike."%${term}%",title.ilike."%${term}%",source_title.ilike."%${term}%"`)
      const { data, error } = await q
      if (error) fail(error, 'Could not load your thoughts.')
      return ((data ?? []) as Thought[]).map((t) => ({ ...t, body: t.body ?? '' }))
    },

    async countThoughts() {
      const me = await requireUid()
      const { count, error } = await sb.from('thoughts').select('id', { count: 'exact', head: true }).eq('owner_id', me)
      if (error) fail(error, 'Could not count your thoughts.')
      return count ?? 0
    },

    async thoughtCountsByDay(days) {
      const me = await requireUid()
      const { data, error } = await sb
        .from('thoughts')
        .select('created_at')
        .eq('owner_id', me)
        .gte('created_at', startOfDay(days - 1).toISOString())
        .limit(2000)
      if (error) fail(error, 'Could not load this week.')
      const counts = Array.from({ length: days }, () => 0)
      for (const row of (data ?? []) as Array<{ created_at: string }>) {
        const n = calendarDaysAgo(row.created_at)
        if (n >= 0 && n < days) counts[n]++
      }
      return counts
    },

    async thoughtImageUrl(path) {
      const { data } = await sb.storage.from(THOUGHT_IMAGE_BUCKET).createSignedUrl(path, 3600)
      return data?.signedUrl ?? null
    },

    async importThoughtImage(path) {
      const { data: blob, error } = await sb.storage.from(THOUGHT_IMAGE_BUCKET).download(path)
      if (error || !blob) return null
      const typed = blob.type.startsWith('image/') ? blob : new Blob([blob], { type: `image/${imageExtension(path) === 'jpg' ? 'jpeg' : imageExtension(path)}` })
      try {
        return await backend.uploadImage(typed)
      } catch {
        return null
      }
    },

    async listCollections() {
      const me = await requireUid()
      const withCounts = await sb
        .from('collections')
        // Slug, cover and description too, for the collection cards a writing can link (CollectionBlock).
        .select('id,name,is_public,cover_color,public_slug,cover_image_public_url,description,collection_thoughts(count)')
        .eq('owner_id', me)
        .order('updated_at', { ascending: false })
      if (!withCounts.error) {
        type Row = Omit<CollectionSummary, 'thought_count' | 'cover_url'> & {
          cover_image_public_url?: string | null
          collection_thoughts: Array<{ count: number }>
        }
        return ((withCounts.data ?? []) as Row[]).map(({ collection_thoughts, cover_image_public_url, ...c }) => ({
          ...c,
          cover_url: cover_image_public_url ?? null,
          thought_count: collection_thoughts?.[0]?.count ?? 0,
        }))
      }
      const { data, error } = await sb
        .from('collections')
        .select('id,name,is_public,cover_color')
        .eq('owner_id', me)
        .order('updated_at', { ascending: false })
      if (error) fail(error, 'Could not load your collections.')
      return ((data ?? []) as Omit<CollectionSummary, 'thought_count'>[]).map((c) => ({ ...c, thought_count: 0 }))
    },

    async uploadImage(file) {
      const me = await requireUid()
      assertUploadableImage(file)
      const path = `${me}/${randomName()}.${imageExtension(file.type)}`
      const { error } = await sb.storage.from(IMAGE_BUCKET).upload(path, file, { contentType: file.type, upsert: false })
      if (error) fail(error, 'Could not upload that image.')
      return sb.storage.from(IMAGE_BUCKET).getPublicUrl(path).data.publicUrl
    },

    async uploadAudio(file) {
      const me = await requireUid()
      assertUploadableAudio(file)
      const type = baseAudioType(file.type)
      const path = `${me}/${randomName()}.${audioExtension(type)}`
      const { error } = await sb.storage.from(AUDIO_BUCKET).upload(path, file, { contentType: type, upsert: false })
      if (error) fail(error, 'Could not upload that recording.')
      return sb.storage.from(AUDIO_BUCKET).getPublicUrl(path).data.publicUrl
    },

    async listVersions(writingId) {
      const { data, error } = await sb
        .from('writing_versions')
        .select(VERSION_COLUMNS)
        .eq('writing_id', writingId)
        .order('created_at', { ascending: false })
        .limit(100)
      if (error) fail(error, 'Could not load history.')
      return (data ?? []) as WritingVersion[]
    },

    async snapshotWriting(writingId, reason) {
      const { error } = await sb.rpc('snapshot_writing', { p_writing_id: writingId, p_reason: reason })
      if (error) fail(error, 'Could not save a version.')
    },

    async restoreVersion(writingId, versionId) {
      const { data: v, error: readErr } = await sb
        .from('writing_versions')
        .select(VERSION_COLUMNS)
        .eq('id', versionId)
        .eq('writing_id', writingId)
        .maybeSingle()
      if (readErr) fail(readErr, 'Could not load that version.')
      if (!v) throw new Error('That version no longer exists.')
      await backend.snapshotWriting(writingId, 'restore')
      const version = v as WritingVersion
      const { data, error } = await sb
        .from('writings')
        .update({
          title: version.title,
          subtitle: version.subtitle,
          cover_url: version.cover_url,
          cover_frame: version.cover_frame ?? null,
          blocks: version.blocks,
          narration: version.narration,
          word_count: version.word_count,
        })
        .eq('id', writingId)
        .select(WRITING_COLUMNS)
        .single()
      if (error) fail(error, 'Could not restore that version.')
      return data as Writing
    },

    async getPublished(handle, slug) {
      const { data, error } = await sb.rpc('get_published_writing', { p_handle: handle, p_slug: slug })
      if (error) fail(error, 'Could not load this writing.')
      return (data as PublishedPayload | null) ?? null
    },

    async saveQuote(writingId, blockId, pageUrl, collectionId) {
      const { data, error } = await sb.rpc('save_writing_quote', {
        p_writing_id: writingId,
        p_block_id: blockId,
        p_source_url: pageUrl,
        p_collection_id: collectionId ?? null,
      })
      if (error) fail(error, 'Could not save that quote.')
      return data as string
    },

    async saveLine(writingId, blockId, text, pageUrl, collectionId) {
      const { data, error } = await sb.rpc('save_writing_line', {
        p_writing_id: writingId,
        p_block_id: blockId,
        p_text: text,
        p_source_url: pageUrl,
        p_collection_id: collectionId ?? null,
      })
      if (error) fail(error, 'Could not take that line.')
      return data as string
    },

    async refileTakenThought(thoughtId, collectionId) {
      const { error } = await sb.rpc('refile_taken_thought', { p_thought_id: thoughtId, p_collection_id: collectionId })
      if (error) fail(error, 'Could not move that card.')
    },

    async createCollection(name) {
      const me = await requireUid()
      const clean = name.trim().slice(0, 120)
      if (!clean) throw new Error('Give the collection a name.')
      const { data, error } = await sb
        .from('collections')
        .insert({ owner_id: me, name: clean })
        .select('id,name,is_public,cover_color')
        .single()
      if (error) fail(error, 'Could not make that collection.')
      return { ...(data as Omit<CollectionSummary, 'thought_count'>), thought_count: 0 }
    },

    async setFollowing(authorId, follow) {
      const me = await requireUid()
      const { error } = follow
        ? await sb.from('follows').insert({ follower_user_id: me, followed_user_id: authorId })
        : await sb.from('follows').delete().eq('follower_user_id', me).eq('followed_user_id', authorId)
      if (error && error.code !== '23505') fail(error, 'Could not update Think Along.')
    },
  }

  return backend
}
